/* eslint-disable import/no-extraneous-dependencies */
import PropTypes from 'prop-types';
import { memo, useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
// LiveKit
import {
  LiveKitRoom,
  VideoConference,
  RoomAudioRenderer,
  useParticipants,
  useSpeakingParticipants,
  useLocalParticipant,
  ParticipantContext,
  ParticipantTile,
  VideoTrack,
  useTracks,
  useConnectionState,
  useRoomContext,
  AudioTrack,
} from '@livekit/components-react';
import { Track, ConnectionState, RoomEvent } from 'livekit-client';
import '@livekit/components-styles';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import Popover from '@mui/material/Popover';
import { useTheme, alpha } from '@mui/material/styles';
// hooks
import { useChatEmitter } from 'src/hooks/useChatSocket';
import { HOST_API } from 'src/config-global';
// components
import Iconify from 'src/components/iconify';
import { LoadingScreen } from 'src/components/loading-screen';
import ChatCallPip from './chat-call-pip';

// ----------------------------------------------------------------------

/**
 * Sits inside <LiveKitRoom> and watches the participant list.
 * When everyone else has left (remote participants = 0 for a brief settled
 * period), it emits call:group-end on the socket so every OTHER browser tab
 * that still shows the "Join" banner receives the event and clears it.
 */
function RoomWatcher({ conversationId, onAllLeft }) {
  const participants = useParticipants();

  useEffect(() => {
    // participants here = REMOTE participants (excludes local user)
    if (participants.length === 0) {
      // Short debounce — LiveKit briefly shows 0 during reconnects
      const t = setTimeout(() => onAllLeft(), 3000);
      return () => clearTimeout(t);
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participants.length]);

  return null;
}

RoomWatcher.propTypes = {
  conversationId: PropTypes.string,
  onAllLeft: PropTypes.func,
};

// ----------------------------------------------------------------------

/**
 * Renders a live call duration timer.
 * Must be mounted inside <LiveKitRoom> to use the useConnectionState hook.
 * Starts counting from the moment the room reaches the Connected state.
 */
function CallDurationTimer({ minimized, isPip }) {
  const connectionState = useConnectionState();
  const [seconds, setSeconds] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (connectionState === ConnectionState.Connected) {
      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => {
          setSeconds((prev) => prev + 1);
        }, 1000);
      }
    } else if (intervalRef.current) {
      // Pause on disconnect / reconnect but keep elapsed time
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [connectionState]);

  const formatDuration = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    }
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  if (connectionState !== ConnectionState.Connected && seconds === 0) return null;

  let topValue = 16;
  if (isPip) topValue = 48;
  else if (minimized) topValue = 8;

  let fontSizeValue = 13;
  if (isPip) fontSizeValue = 14;
  else if (minimized) fontSizeValue = 11;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: topValue,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 6,
        px: 1.5,
        py: 0.4,
        borderRadius: 5,
        bgcolor: (t) => alpha(t.palette.common.black, 0.45),
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255,255,255,0.12)',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
      }}
    >
      {/* Live dot */}
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: connectionState === ConnectionState.Connected ? 'success.main' : 'warning.main',
          animation: connectionState === ConnectionState.Connected
            ? 'timer-pulse 2s ease-in-out infinite'
            : 'none',
          '@keyframes timer-pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.3 },
          },
        }}
      />
      <Typography
        sx={{
          color: 'common.white',
          fontWeight: 'bold',
          fontSize: fontSizeValue,
          letterSpacing: 1.5,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1,
        }}
      >
        {formatDuration(seconds)}
      </Typography>
    </Box>
  );
}

CallDurationTimer.propTypes = {
  minimized: PropTypes.bool,
  isPip: PropTypes.bool,
};

// ----------------------------------------------------------------------

/**
 * Sits inside <LiveKitRoom> and emits call:participant-join / call:participant-leave
 * via the chat socket so the server can broadcast live participant lists.
 * Renders nothing — purely a side-effect component.
 */
function CallPresenceEmitter({ conversationId, userInfo, emitCallEvent }) {
  const connectionState = useConnectionState();
  const joinedRef = useRef(false);

  useEffect(() => {
    if (connectionState === ConnectionState.Connected && !joinedRef.current) {
      joinedRef.current = true;
      emitCallEvent('call:participant-join', { conversationId, userInfo });
    }
    if (connectionState === ConnectionState.Disconnected && joinedRef.current) {
      joinedRef.current = false;
      emitCallEvent('call:participant-leave', { conversationId });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionState]);

  // Emit leave on unmount (user closes or navigates away)
  useEffect(() => () => {
    if (joinedRef.current) {
      emitCallEvent('call:participant-leave', { conversationId });
      joinedRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

CallPresenceEmitter.propTypes = {
  conversationId: PropTypes.string,
  userInfo: PropTypes.object,
  emitCallEvent: PropTypes.func,
};

// ----------------------------------------------------------------------

function CallControls({ 
  minimized, 
  onMinimize, 
  onDisconnect, 
  theme,
  isPip,
  onTogglePip 
}) {
  const { isMicrophoneEnabled, isCameraEnabled, localParticipant } = useLocalParticipant();

  const toggleMic = async () => {
    await localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled);
  };

  const toggleCam = async () => {
    await localParticipant.setCameraEnabled(!isCameraEnabled);
  };

  const handleNativePip = async () => {
    // Basic native Video PiP fallback (if Document PiP is not used/possible)
    const popover = document.querySelector('.call-popover-wrapper');
    const targetVideo = popover?.querySelector('video') || document.querySelector('video');
    
    if (targetVideo && document.pictureInPictureEnabled) {
      try {
        if (targetVideo !== document.pictureInPictureElement) {
          await targetVideo.requestPictureInPicture();
        } else {
          await document.exitPictureInPicture();
        }
      } catch (e) {
        console.error('Video PiP failed:', e);
      }
    }
  };

  const { isScreenShareEnabled } = useLocalParticipant();
  const tracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare]);
  const hasVideo = tracks.length > 0;

  const toggleScreenShare = async () => {
    try {
      await localParticipant.setScreenShareEnabled(!isScreenShareEnabled);
    } catch (e) {
      console.error('Screen share failed:', e);
    }
  };

  // Support for Document Picture-in-Picture (Google Meet style)
  const supportsDocumentPip = !!window.documentPictureInPicture;

  let controlsSx = {};

  if (isPip) {
    controlsSx = {
      bottom: 20,
      left: '50%',
      transform: 'translateX(-50%)',
    };
  } else if (minimized) {
    controlsSx = {
      bottom: 12,
      left: '50%',
      transform: 'translateX(-50%)',
      opacity: 0,
      visibility: 'hidden',
      bgcolor: alpha(theme.palette.common.black, 0.4),
      backdropFilter: 'blur(8px)',
      p: 0.5,
      borderRadius: '24px',
    };
  } else {
    // Full screen / normal view
    controlsSx = {
      bottom: 24,
      left: '50%',
      transform: 'translateX(-50%)',
      bgcolor: alpha(theme.palette.common.black, 0.5),
      backdropFilter: 'blur(16px)',
      p: 1.5,
      borderRadius: '32px',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      boxShadow: theme.customShadows.z24,
    };
  }

  const isSmall = minimized || isPip;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      spacing={isSmall ? 1 : 2}
      className="call-controls-bar"
      sx={{
        position: 'absolute',
        zIndex: 5,
        transition: (t) => t.transitions.create(['opacity', 'visibility', 'bottom', 'transform']),
        ...controlsSx,
      }}
    >
      {/* Microphone Button */}
      <IconButton
        size={isSmall ? "small" : "medium"}
        onClick={toggleMic}
        sx={{
          bgcolor: isMicrophoneEnabled ? alpha(theme.palette.common.black, 0.5) : alpha(theme.palette.error.main, 0.8),
          color: 'common.white',
          '&:hover': { bgcolor: isMicrophoneEnabled ? alpha(theme.palette.common.black, 0.8) : theme.palette.error.main },
          width: isSmall ? 36 : 48,
          height: isSmall ? 36 : 48,
        }}
      >
        <Iconify icon={isMicrophoneEnabled ? 'solar:microphone-bold' : 'solar:muted-bold'} width={isSmall ? 18 : 24} />
      </IconButton>

      {/* Camera Button */}
      <IconButton
        size={isSmall ? "small" : "medium"}
        onClick={toggleCam}
        sx={{
          bgcolor: isCameraEnabled ? alpha(theme.palette.common.black, 0.5) : alpha(theme.palette.error.main, 0.8),
          color: 'common.white',
          '&:hover': { bgcolor: isCameraEnabled ? alpha(theme.palette.common.black, 0.8) : theme.palette.error.main },
          width: isSmall ? 36 : 48,
          height: isSmall ? 36 : 48,
        }}
      >
        <Iconify icon={isCameraEnabled ? 'solar:videocamera-bold' : 'solar:videocamera-record-bold'} width={isSmall ? 18 : 24} />
      </IconButton>
      
      {/* Screen Share Button */}
      <IconButton
        size={isSmall ? "small" : "medium"}
        onClick={toggleScreenShare}
        sx={{
          bgcolor: isScreenShareEnabled ? theme.palette.success.main : alpha(theme.palette.common.black, 0.5),
          color: 'common.white',
          '&:hover': { bgcolor: isScreenShareEnabled ? theme.palette.success.dark : alpha(theme.palette.common.black, 0.8) },
          width: isSmall ? 36 : 48,
          height: isSmall ? 36 : 48,
        }}
      >
        <Iconify icon="solar:share-circle-bold" width={isSmall ? 18 : 24} />
      </IconButton>

      {/* PiP Button */}
      {supportsDocumentPip ? (
         <IconButton
            size={isSmall ? "small" : "medium"}
            onClick={onTogglePip}
            sx={{
              bgcolor: isPip ? theme.palette.primary.main : alpha(theme.palette.common.black, 0.5),
              color: 'common.white',
              '&:hover': { bgcolor: isPip ? theme.palette.primary.dark : alpha(theme.palette.common.black, 0.8) },
              width: isSmall ? 36 : 48,
              height: isSmall ? 36 : 48,
            }}
          >
            <Iconify icon="solar:window-frame-bold" width={isSmall ? 18 : 24} />
          </IconButton>
      ) : hasVideo && (
        <IconButton
          size={isSmall ? "small" : "medium"}
          onClick={handleNativePip}
          sx={{
            bgcolor: alpha(theme.palette.common.black, 0.5),
            color: 'common.white',
            '&:hover': { bgcolor: alpha(theme.palette.common.black, 0.8) },
            width: isSmall ? 36 : 48,
            height: isSmall ? 36 : 48,
          }}
        >
          <Iconify icon="solar:pip-bold" width={isSmall ? 18 : 24} />
        </IconButton>
      )}

      {/* Minimize Button */}
      {!isPip && (
        <IconButton
          size={isSmall ? "small" : "medium"}
          onClick={onMinimize}
          sx={{
            bgcolor: alpha(theme.palette.common.black, 0.5),
            color: 'common.white',
            '&:hover': { bgcolor: alpha(theme.palette.common.black, 0.8) },
            width: isSmall ? 36 : 48,
            height: isSmall ? 36 : 48,
          }}
        >
          <Iconify icon={minimized ? 'solar:maximize-bold' : 'solar:minimize-bold'} width={isSmall ? 18 : 24} />
        </IconButton>
      )}
      
      {/* End Call Button */}
      <IconButton
        size={isSmall ? "small" : "medium"}
        onClick={onDisconnect}
        sx={{
          bgcolor: alpha(theme.palette.error.main, 0.8),
          color: 'common.white',
          '&:hover': { bgcolor: theme.palette.error.main },
          width: isSmall ? 36 : 48,
          height: isSmall ? 36 : 48,
        }}
      >
        <Iconify icon="solar:phone-bold" sx={{ transform: 'rotate(135deg)' }} width={isSmall ? 18 : 24} />
      </IconButton>
    </Stack>
  );
}

CallControls.propTypes = {
  minimized: PropTypes.bool,
  onMinimize: PropTypes.func,
  onDisconnect: PropTypes.func,
  theme: PropTypes.object,
  isPip: PropTypes.bool,
  onTogglePip: PropTypes.func,
};

// ----------------------------------------------------------------------

/**
 * Renders remote audio tracks with custom participant volumes.
 */
function CustomAudioRenderer({ participantVolumes }) {
  const audioTracks = useTracks([
    Track.Source.Microphone,
    Track.Source.ScreenShareAudio
  ]);

  const remoteAudioTracks = audioTracks.filter(
    (tr) => !tr.participant.isLocal && tr.participant.identity !== 'local'
  );

  return (
    <>
      {remoteAudioTracks.map((trackRef) => {
        const vol = participantVolumes[trackRef.participant.sid] !== undefined
          ? participantVolumes[trackRef.participant.sid]
          : 1;

        return (
          <AudioTrack
            key={trackRef.publication.trackSid}
            trackRef={trackRef}
            volume={vol}
          />
        );
      })}
    </>
  );
}

CustomAudioRenderer.propTypes = {
  participantVolumes: PropTypes.object.isRequired,
};

// ----------------------------------------------------------------------

function CustomParticipantTile({
  participant,
  conversation,
  theme,
  volume,
  onVolumeChange,
  tracks,
}) {
  const isLocal = participant.isLocal || participant.identity === 'local';

  // Find camera track for this participant
  const cameraTrack = tracks.find(
    (tr) => tr.participant.sid === participant.sid && tr.source === Track.Source.Camera
  );

  const isVideoEnabled = participant.isCameraEnabled && cameraTrack;

  // Popover state for volume adjustment
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenVolume = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseVolume = () => {
    setAnchorEl(null);
  };

  const openVolume = Boolean(anchorEl);

  const isSpeaking = participant.isSpeaking;
  const isMuted = !participant.isMicrophoneEnabled;
  const avatarSize = 96;

  // Resolve display name & avatar
  const name = isLocal ? 'You' : (participant.name || participant.identity || 'Participant');

  let volumeIcon = 'solar:volume-loud-bold';
  if (volume === 0) {
    volumeIcon = 'solar:muted-bold';
  } else if (volume < 40) {
    volumeIcon = 'solar:volume-low-bold';
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: 1,
        height: 1,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.grey[900], 0.95),
        border: `solid 2px ${isSpeaking ? theme.palette.success.main : alpha(theme.palette.common.white, 0.08)}`,
        boxShadow: isSpeaking 
          ? `0 0 20px ${alpha(theme.palette.success.main, 0.4)}` 
          : 'none',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: theme.transitions.create(['border-color', 'box-shadow']),
      }}
    >
      {/* Video stream or Avatar fallback */}
      {isVideoEnabled ? (
        <VideoTrack
          trackRef={cameraTrack}
          sx={{
            width: 1,
            height: 1,
            objectFit: 'cover',
          }}
        />
      ) : (
        <Stack alignItems="center" spacing={2}>
          <Avatar
            sx={{
              width: avatarSize,
              height: avatarSize,
              fontSize: '2rem',
              bgcolor: isSpeaking ? 'success.main' : 'primary.main',
              border: `solid 4px ${isSpeaking ? theme.palette.success.main : alpha(theme.palette.common.white, 0.12)}`,
              boxShadow: isSpeaking ? `0 0 30px ${alpha(theme.palette.success.main, 0.4)}` : 'none',
              animation: isSpeaking ? 'speaking-pulse 1.5s infinite ease-in-out' : 'none',
              '@keyframes speaking-pulse': {
                '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                '50%': { transform: 'scale(1.05)', opacity: 0.8 },
              },
            }}
          >
            {name.charAt(0).toUpperCase()}
          </Avatar>
        </Stack>
      )}

      {/* Top Right Action: Individual volume controls (Remote only) */}
      {!isLocal && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 3,
          }}
        >
          <IconButton
            size="small"
            onClick={handleOpenVolume}
            sx={{
              bgcolor: alpha(theme.palette.common.black, 0.6),
              color: 'common.white',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.08)',
              '&:hover': {
                bgcolor: alpha(theme.palette.common.black, 0.8),
              },
            }}
          >
            <Iconify 
              icon={volumeIcon} 
              width={16} 
            />
          </IconButton>

          <Popover
            open={openVolume}
            anchorEl={anchorEl}
            onClose={handleCloseVolume}
            onClick={(e) => e.stopPropagation()} // Prevent card click behaviors
            sx={{ zIndex: 1600 }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                p: 2,
                width: 220,
                bgcolor: alpha(theme.palette.grey[900], 0.95),
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'common.white',
                boxShadow: theme.customShadows.z24,
              },
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>User Volume</span>
              <span style={{ opacity: 0.6, fontSize: '0.8rem' }}>{volume}%</span>
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <IconButton 
                size="small" 
                onClick={() => onVolumeChange(volume === 0 ? 100 : 0)}
                sx={{ color: 'common.white', p: 0.5 }}
              >
                <Iconify icon={volume === 0 ? 'solar:muted-bold' : 'solar:volume-low-bold'} />
              </IconButton>
              <Slider
                value={volume}
                onChange={(e, val) => onVolumeChange(val)}
                min={0}
                max={100}
                size="small"
                sx={{
                  color: 'primary.main',
                  '& .MuiSlider-thumb': {
                    width: 12,
                    height: 12,
                  },
                }}
              />
            </Stack>
          </Popover>
        </Box>
      )}

      {/* Bottom Bar overlay: Name & Status indicators */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 12,
          left: 12,
          right: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        {/* Name Tag */}
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.common.black, 0.65),
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {isSpeaking && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'success.main',
                animation: 'speaking-dot-pulse 1s infinite ease-in-out',
                '@keyframes speaking-dot-pulse': {
                  '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.3)', opacity: 0.5 },
                },
              }}
            />
          )}
          <Typography variant="subtitle2" sx={{ color: 'common.white', fontWeight: 'semibold', fontSize: '0.85rem' }}>
            {name}
          </Typography>
        </Box>

        {/* Status Indicators */}
        <Stack direction="row" spacing={0.5} sx={{ pointerEvents: 'auto' }}>
          {isMuted && (
            <Box
              sx={{
                p: 0.5,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.error.main, 0.75),
                backdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Iconify icon="solar:muted-bold" width={14} sx={{ color: 'common.white' }} />
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

CustomParticipantTile.propTypes = {
  participant: PropTypes.object.isRequired,
  conversation: PropTypes.object,
  theme: PropTypes.object.isRequired,
  volume: PropTypes.number.isRequired,
  onVolumeChange: PropTypes.func.isRequired,
  tracks: PropTypes.array.isRequired,
};

// ----------------------------------------------------------------------

function CustomConferenceView({ conversation, participantVolumes, onVolumeChange }) {
  const participants = useParticipants({ sorted: true });
  const theme = useTheme();

  const tracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare]);
  const screenShareTrack = tracks.find((tr) => tr.source === Track.Source.ScreenShare);

  const totalParticipants = participants.length;

  const getGridLayout = (count) => {
    if (count <= 1) return { columns: '1fr', rows: '1fr' };
    if (count === 2) return { columns: 'repeat(2, 1fr)', rows: '1fr' };
    if (count <= 4) return { columns: 'repeat(2, 1fr)', rows: 'repeat(2, 1fr)' };
    if (count <= 6) return { columns: 'repeat(3, 1fr)', rows: 'repeat(2, 1fr)' };
    if (count <= 9) return { columns: 'repeat(3, 1fr)', rows: 'repeat(3, 1fr)' };
    return { columns: 'repeat(4, 1fr)', rows: 'auto' };
  };

  const { columns, rows } = getGridLayout(totalParticipants);

  if (screenShareTrack) {
    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{
          width: 1,
          height: 1,
          p: 2,
          pb: 12, // Space for controls
          bgcolor: 'common.black',
        }}
      >
        {/* Main Focus Area: Screen Share */}
        <Box
          sx={{
            flexGrow: 1,
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: alpha(theme.palette.grey[900], 0.95),
            border: `solid 1px ${alpha(theme.palette.common.white, 0.08)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <VideoTrack
            trackRef={screenShareTrack}
            sx={{
              width: 1,
              height: 1,
              objectFit: 'contain',
            }}
          />
          {/* Label for Screen Share */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              px: 2,
              py: 0.75,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.common.black, 0.75),
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'common.white', fontWeight: 'semibold' }}>
              {screenShareTrack.participant.name || screenShareTrack.participant.identity}{"'s Screen"}
            </Typography>
          </Box>
        </Box>

        {/* Sidebar/Carousel: Other Participant Tiles */}
        <Box
          sx={{
            width: { xs: 1, md: 240 },
            height: { xs: 160, md: 1 },
            display: 'flex',
            flexDirection: { xs: 'row', md: 'column' },
            gap: 1.5,
            overflowY: { xs: 'hidden', md: 'auto' },
            overflowX: { xs: 'auto', md: 'hidden' },
            pr: { xs: 0, md: 0.5 },
          }}
        >
          {participants.map((participant) => (
            <Box
              key={participant.sid}
              sx={{
                width: { xs: 200, md: 1 },
                height: { xs: 1, md: 140 },
                flexShrink: 0,
              }}
            >
              <CustomParticipantTile
                participant={participant}
                conversation={conversation}
                theme={theme}
                volume={participantVolumes[participant.sid] !== undefined ? participantVolumes[participant.sid] * 100 : 100}
                onVolumeChange={(val) => onVolumeChange(participant.sid, val)}
                tracks={tracks}
              />
            </Box>
          ))}
        </Box>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        width: 1,
        height: 1,
        display: 'grid',
        gridTemplateColumns: columns,
        gridTemplateRows: rows,
        gap: 2,
        p: 2,
        pb: 12, // Space for controls
        bgcolor: 'common.black',
      }}
    >
      {participants.map((participant) => (
        <CustomParticipantTile
          key={participant.sid}
          participant={participant}
          conversation={conversation}
          theme={theme}
          volume={participantVolumes[participant.sid] !== undefined ? participantVolumes[participant.sid] * 100 : 100}
          onVolumeChange={(val) => onVolumeChange(participant.sid, val)}
          tracks={tracks}
        />
      ))}
    </Box>
  );
}

CustomConferenceView.propTypes = {
  conversation: PropTypes.object,
  participantVolumes: PropTypes.object.isRequired,
  onVolumeChange: PropTypes.func.isRequired,
};

// ----------------------------------------------------------------------

function MinimizedParticipantView({ conversation, theme, isPip }) {
  const participants = useParticipants({ sorted: true });
  // Use specialized speaker hook for reactive speaker updates
  const activeSpeaker = useSpeakingParticipants()[0];
  
  // Track the last remote speaker to avoid showing self-view when it goes quiet
  const lastRemoteSpeakerSidRef = useRef(null);

  /**
   * Determine the participant to display in the minimized/PiP view.
   * Logic:
   * 1. If someone (Remote) is currently speaking, show them.
   * 2. If someone (Remote) is screen sharing, show them.
   * 3. If neither, show the last person (Remote) who was speaking.
   * 4. Fallback to the first remote participant found.
   * 5. If no remote participants exist at all, show the local user.
   */
  const remoteParticipants = participants.filter(p => p.identity !== 'local');
  
  // Use effect to keep track of the last remote speaker
  useEffect(() => {
    const currentRemoteSpeaker = remoteParticipants.find(p => p.isSpeaking);
    if (currentRemoteSpeaker) {
      lastRemoteSpeakerSidRef.current = currentRemoteSpeaker.sid;
    }
  }, [remoteParticipants]);

  const lastRemoteSpeaker = remoteParticipants.find(p => p.sid === lastRemoteSpeakerSidRef.current);
  const screenSharer = remoteParticipants.find(p => p.isScreenShareEnabled);

  /**
   * Selection Logic (User Preference):
   * 1. Any remote SCREEN SHARE (Priority 1)
   * 2. Current remote SPEAKER (Priority 2)
   * 3. Last remote SPEAKER (Priority 3)
   * 4. Any other remote participant
   * 5. Local participant (only if alone)
   */
  const activeParticipant = screenSharer ||
                           (activeSpeaker && activeSpeaker.identity !== 'local' ? activeSpeaker : null) || 
                           lastRemoteSpeaker || 
                           remoteParticipants[0] ||
                           participants.find(p => p.identity === 'local');

  // Look for both camera and screen share tracks
  const tracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare]);
  
  // Prioritize screen share track if available for this participant
  const videoTrack = tracks.find(tr => tr.participant.sid === activeParticipant?.sid && tr.source === Track.Source.ScreenShare) ||
                     tracks.find(tr => tr.participant.sid === activeParticipant?.sid && tr.source === Track.Source.Camera);

  if (!activeParticipant) return null;

  // Determine if we should show video or avatar
  const isVideoShown = !!videoTrack && (
    (videoTrack.source === Track.Source.ScreenShare && activeParticipant.isScreenShareEnabled) ||
    (videoTrack.source === Track.Source.Camera && activeParticipant.isCameraEnabled)
  );

  const avatarSize = isPip ? 160 : 72;

  return (
    <Box
      sx={{
        width: 1,
        height: 1,
        position: 'relative',
        bgcolor: 'common.black',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {isVideoShown ? (
          <VideoTrack 
            trackRef={videoTrack}
            sx={{
              width: 1,
              height: 1,
              objectFit: videoTrack.source === Track.Source.ScreenShare ? 'contain' : 'cover',
            }}
          />
      ) : (
         <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              zIndex: 0,
            }}
          >
            <Avatar
              src={activeParticipant.identity === 'local' ? (conversation?.avatar || '') : ''}
              sx={{
                width: avatarSize,
                height: avatarSize,
                border: (t) => `solid 4px ${activeParticipant.isSpeaking ? t.palette.success.main : t.palette.primary.main}`,
                boxShadow: (t) => activeParticipant.isSpeaking 
                    ? `0 0 ${isPip ? '40px' : '20px'} ${alpha(t.palette.success.main, 0.6)}` 
                    : 'none',
                animation: activeParticipant.isSpeaking ? 'speaking-pulse 1.5s infinite ease-in-out' : 'none',
                '@keyframes speaking-pulse': {
                  '0%': { transform: 'scale(1)', boxShadow: `0 0 0px ${alpha(theme.palette.success.main, 0)}` },
                  '50%': { transform: 'scale(1.05)', boxShadow: `0 0 ${isPip ? '50px' : '25px'} ${alpha(theme.palette.success.main, 0.5)}` },
                  '100%': { transform: 'scale(1)', boxShadow: `0 0 0px ${alpha(theme.palette.success.main, 0)}` },
                },
              }}
            >
               {(activeParticipant.name || activeParticipant.identity || '?').charAt(0).toUpperCase()}
            </Avatar>
         </Box>
      )}

      {/* Top Overlays */}
      <Stack 
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{
          position: 'absolute',
          top: 10,
          left: 10,
          right: 10,
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        {/* Name (Top Left) */}
        <Box
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: 0.75,
            bgcolor: alpha(theme.palette.common.black, 0.4),
            backdropFilter: 'blur(4px)',
          }}
        >
          <Typography variant="caption" sx={{ color: 'common.white', fontWeight: 'bold' }}>
            {activeParticipant.identity === 'local' ? 'You' : (activeParticipant.name || 'Participant')}
          </Typography>
        </Box>

        {/* Speaking Indicator (Top Right) */}
        {activeParticipant.isSpeaking && (
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 0.75,
              bgcolor: alpha(theme.palette.success.main, 0.15),
              border: (t) => `solid 1px ${alpha(t.palette.success.main, 0.3)}`,
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              animation: 'pulse-bg 2s infinite ease-in-out',
              '@keyframes pulse-bg': {
                '0%': { opacity: 0.8 },
                '50%': { opacity: 1 },
                '100%': { opacity: 0.8 },
              }
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                bgcolor: 'success.main',
                borderRadius: '50%',
              }}
            />
            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold', fontSize: '0.65rem', textTransform: 'uppercase' }}>
              Speaking
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

MinimizedParticipantView.propTypes = {
  conversation: PropTypes.object,
  theme: PropTypes.object,
  isPip: PropTypes.bool,
};

// ----------------------------------------------------------------------

function ChatRoomGroupCall({
  open,
  onClose,
  onMinimize,
  minimized,
  onAccept,
  token,
  url,
  conversationId,
  callType,
  isIncoming,
  conversation,
  // PiP specific props
  isPip,
  pipWindow,
  onTogglePip,
  onPipClose,
}) {
  const theme = useTheme();
  const { emitCallEvent } = useChatEmitter();
  const { user } = useSelector((s) => s.auth);

  const [participantVolumes, setParticipantVolumes] = useState({});

  const handleVolumeChange = useCallback((participantSid, value) => {
    const volumeValue = value / 100;
    setParticipantVolumes((prev) => ({
      ...prev,
      [participantSid]: volumeValue,
    }));
  }, []);

  // Build a userInfo object to broadcast to other participants
  const userInfo = {
    userId: user?.id,
    name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.username,
    avatarUrl: user?.photoURL || user?.avatar || '',
  };

  // Called by LiveKit's onDisconnected — just notify parent to clean up local state.
  const handleDisconnected = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleDecline = useCallback(() => {
    handleDisconnected();
  }, [handleDisconnected]);

  const handleAllLeft = useCallback(() => {
    emitCallEvent('call:group-end', { conversationId });
    handleDisconnected();
  }, [conversationId, emitCallEvent, handleDisconnected]);

  const RING_TIMEOUT = 60;
  const [ringCountdown, setRingCountdown] = useState(RING_TIMEOUT);

  useEffect(() => {
    if (!isIncoming || token || !open) {
      setRingCountdown(RING_TIMEOUT);
      return undefined;
    }
    const interval = setInterval(() => {
      setRingCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleDecline();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isIncoming, token, open, handleDecline]);

  if (isIncoming && !token && open) {
    let displayName;
    let displayAvatar;

    if (conversation?.type === 'group') {
      displayName   = conversation?.name   || 'Group Call';
      displayAvatar = conversation?.avatar || '';
    } else {
      const caller = (conversation?.participants || []).find((p) => {
        const pId = p.userId?.adminRef || p.userId?._id || p.userId;
        return pId?.toString() !== undefined;
      });
      const u = caller?.userId;
      const photoVal = u?.documents?.photo;
      let rawPhoto = null;
      if (Array.isArray(photoVal)) {
        if (photoVal.length > 0) {
          rawPhoto = photoVal[0];
        }
      } else if (photoVal) {
        rawPhoto = photoVal;
      }
      displayName   = `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || 'Incoming Call';
      
      let resolvedPhoto = '';
      if (rawPhoto) {
        if (rawPhoto.startsWith('http')) {
          resolvedPhoto = rawPhoto;
        } else {
          resolvedPhoto = `${HOST_API}/${rawPhoto}`;
        }
      }
      displayAvatar = resolvedPhoto;
    }

    return (
      <Dialog
        fullScreen
        open={open}
        onClose={handleDecline}
        PaperProps={{ sx: { bgcolor: 'common.black', color: 'common.white' } }}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 1,
            height: 1,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.grey[900], 0.8),
            color: 'common.white',
            textAlign: 'center',
            p: 3,
          }}
        >
          <Box sx={{ position: 'relative', mb: 4 }}>
            <Avatar
              src={displayAvatar}
              sx={{
                width: 140,
                height: 140,
                border: `solid 4px ${theme.palette.primary.main}`,
                boxShadow: theme.customShadows.z24,
              }}
            />
            <Box
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'primary.main',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                bottom: 0,
                right: 0,
                border: `solid 3px ${theme.palette.grey[900]}`,
              }}
            >
              <Iconify icon={callType === 'video' ? 'solar:videocamera-bold' : 'solar:phone-bold'} width={20} />
            </Box>
          </Box>
          <Typography variant="h3" sx={{ mb: 1 }}>{displayName}</Typography>
          <Typography variant="h6" sx={{ opacity: 0.7, mb: 1, fontWeight: 'regular' }}>
            Incoming {callType === 'video' ? 'Video' : 'Voice'} Call...
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.45, mb: 7 }}>Auto-dismiss in {ringCountdown}s</Typography>
          <Stack direction="row" spacing={3}>
            <Button variant="contained" color="success" size="large" onClick={onAccept} sx={{ borderRadius: 1.5, px: 6, py: 1.5 }}>Join</Button>
            <Button variant="contained" color="error" size="large" onClick={handleDecline} sx={{ borderRadius: 1.5, px: 6, py: 1.5 }}>Decline</Button>
          </Stack>
        </Stack>
      </Dialog>
    );
  }

  if (!token || !url || !open) return null;

  let containerSx = {
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
  };

  if (isPip) {
    containerSx = {
      width: '100%',
      height: '100%',
    };
  } else if (minimized) {
    containerSx = {
      bottom: 24,
      right: 24,
      width: 320,
      height: 200, // Slightly taller to avoid overlap
      borderRadius: 2,
      border: (t) => `solid 1px ${alpha(t.palette.common.white, 0.12)}`,
    };
  }

  return (
    <Box
      className="call-popover-wrapper"
      sx={{
        position: isPip ? 'relative' : 'fixed',
        zIndex: 1400,
        bgcolor: 'common.black',
        overflow: 'hidden',
        boxShadow: isPip ? 'none' : (t) => t.customShadows.z24,
        transition: (t) => t.transitions.create(['width', 'height', 'bottom', 'right', 'border-radius']),
        ...containerSx,
        '&:hover .call-controls-bar': { opacity: 1, visibility: 'visible' }
      }}
    >
      <LiveKitRoom
        video={callType === 'video'}
        audio
        token={token}
        serverUrl={url}
        onDisconnected={handleDisconnected}
        connect
        data-lk-theme="default"
        style={{ height: '100%' }}
      >
        <ChatCallPip pipWindow={pipWindow}>
          <CallControls 
            minimized={minimized}
            onMinimize={onMinimize}
            onDisconnect={handleDisconnected}
            theme={theme}
            isPip={isPip}
            onTogglePip={onTogglePip}
          />
          <CallDurationTimer minimized={minimized} isPip={isPip} />
          <CallPresenceEmitter conversationId={conversationId} userInfo={userInfo} emitCallEvent={emitCallEvent} />
          <RoomWatcher conversationId={conversationId} onAllLeft={handleAllLeft} />
          {(minimized || isPip) ? (
            <MinimizedParticipantView conversation={conversation} theme={theme} isPip={isPip} />
          ) : (
            <CustomConferenceView 
              conversation={conversation} 
              participantVolumes={participantVolumes} 
              onVolumeChange={handleVolumeChange} 
            />
          )}
          <CustomAudioRenderer participantVolumes={participantVolumes} />
        </ChatCallPip>
      </LiveKitRoom>
    </Box>
  );
}

ChatRoomGroupCall.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onMinimize: PropTypes.func,
  minimized: PropTypes.bool,
  onAccept: PropTypes.func,
  token: PropTypes.string,
  url: PropTypes.string,
  conversationId: PropTypes.string,
  callType: PropTypes.string,
  isIncoming: PropTypes.bool,
  conversation: PropTypes.object,
  isPip: PropTypes.bool,
  pipWindow: PropTypes.object,
  onTogglePip: PropTypes.func,
  onPipClose: PropTypes.func,
};

export default memo(ChatRoomGroupCall);
